import {
  ImpactType,
  OxfordCategory,
  PublicationStatus,
  ProjectSubType,
  ProjectType,
  ProjectUpdateType,
} from '@prisma/client';
import { z } from 'zod';
import { applyProjectTypeRefinement, slugRegex } from './admin-project-validation';

export const impactTypeSchema = z.nativeEnum(ImpactType);
export const projectStatusSchema = z.nativeEnum(PublicationStatus);
export const projectTypeSchema = z.nativeEnum(ProjectType);
export const projectSubTypeSchema = z.nativeEnum(ProjectSubType);
export const oxfordCategorySchema = z.nativeEnum(OxfordCategory);
const projectUpdateTypeSchema = z.nativeEnum(ProjectUpdateType);

export const coreProjectFields = {
  title: z.string().trim().min(1).max(255),
  slug: z.string().trim().min(1).max(255).regex(slugRegex, 'Slug must be kebab-case'),
  impactType: impactTypeSchema,
  projectType: projectTypeSchema,
  projectSubType: projectSubTypeSchema.nullish(),
  oxfordCategory: oxfordCategorySchema.nullish(),
};

const contentFields = {
  description: z.string().trim().min(1),
  shortSnippet: z.string().trim().min(1),
  longSnippet: z.string().trim().min(1),
};

const draftContentFields = {
  description: contentFields.description.nullish(),
  shortSnippet: contentFields.shortSnippet.nullish(),
  longSnippet: contentFields.longSnippet.nullish(),
};

const optionalContentFields = {
  whySupport: z.string().trim().min(1).nullish(),
  impactFact: z.string().nullish(),
};

const mediaFields = {
  primaryImageId: z.number().int(),
};

const draftMediaFields = {
  primaryImageId: mediaFields.primaryImageId.nullish(),
};

const optionalMediaFields = {
  videoLink: z.string().url().nullish(),
  projectAssetsLink: z.string().url().nullish(),
};

const classificationFields = {
  climateBenefits: z.string().nullish(),
  natureBenefits: z.string().nullish(),
  peopleBenefits: z.string().nullish(),
};

const optionalClassificationFields = {
  fundedSince: z.coerce.date().nullish(),
  lastVisit: z.coerce.date().nullish(),
};

export const projectSdgNumberSchema = z.number().int().min(1).max(17);

export const projectSdgWriteSchema = z.object({
  sdgNumber: projectSdgNumberSchema,
  explanation: z.string().nullish(),
});

export const projectSdgsWriteSchema = z
  .array(projectSdgWriteSchema)
  .refine(
    value => new Set(value.map(sdg => sdg.sdgNumber)).size === value.length,
    'Each SDG can only be selected once',
  );

export const optionalProjectFields = {
  ...draftContentFields,
  ...optionalContentFields,
  ...draftMediaFields,
  ...optionalMediaFields,
  ...classificationFields,
  ...optionalClassificationFields,
};

const saveableProject = z.object(coreProjectFields).merge(z.object(optionalProjectFields));

const publishableProject = z.object({
  ...coreProjectFields,
  ...contentFields,
  ...optionalContentFields,
  ...mediaFields,
  ...optionalMediaFields,
  ...classificationFields,
  ...optionalClassificationFields,
});

export const projectPublishableOverviewWriteSchema = applyProjectTypeRefinement(z.object(coreProjectFields), 'publish');

export const projectPublishableContentWriteSchema = z.object({
  ...contentFields,
  ...optionalContentFields,
});

export const projectPublishableClassificationWriteSchema = z.object(classificationFields);

export const projectUpdatesDetailSchema = z.object({
  updates: z.array(
    z.object({
      type: projectUpdateTypeSchema,
      title: z.string().trim().min(1),
      content: z.string().trim().min(1),
      date: z.coerce.date(),
    }),
  ),
});

type ProjectRelationalPublishReadiness = {
  imageCount: number;
  locationCount: number;
  sdgCount: number;
  primaryImageId?: number | null;
};

export const isProjectMediaPublishReady = ({
  imageCount,
  primaryImageId,
}: Pick<ProjectRelationalPublishReadiness, 'imageCount' | 'primaryImageId'>) =>
  imageCount > 0 && primaryImageId != null;

export const isProjectLocationsPublishReady = ({
  locationCount,
}: Pick<ProjectRelationalPublishReadiness, 'locationCount'>) => locationCount > 0;

export const isProjectClassificationPublishReady = ({
  sdgCount,
}: Pick<ProjectRelationalPublishReadiness, 'sdgCount'>) => sdgCount > 0;

export const getProjectRelationalPublishRequirements = ({
  imageCount,
  locationCount,
  sdgCount,
  primaryImageId,
}: ProjectRelationalPublishReadiness) => [
  ...(sdgCount > 0 ? [] : ['at least one SDG']),
  ...(locationCount > 0 ? [] : ['at least one location']),
  ...(imageCount > 0 ? [] : ['at least one image']),
  ...(primaryImageId != null ? [] : ['a primary image']),
];

// Create (DRAFT): Core fields are mandatory, the rest are optional
export const draftProjectCreateSchema = applyProjectTypeRefinement(saveableProject, 'draft');

// Update (DRAFT): All fields optional
export const draftProjectUpdateSchema = applyProjectTypeRefinement(saveableProject.partial(), 'draft');

// Publish: All required fields are mandatory; subType required when options exist
export const liveProjectCreateSchema = applyProjectTypeRefinement(publishableProject, 'publish');
